import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Car, Search, Wrench } from "lucide-react";
import type { UserListEntry } from "@/lib/user-analytics";
import { SafeImage } from "@/components/ui/SafeImage";
import { Input } from "@/components/ui/Input";
import { Workspace, WorkspaceSection, FieldLabel } from "@/components/ui/Workspace";
import { formatDate, formatNumber, imageSrc, userDisplayName } from "@/lib/utils";

function UserAvatar({ user }: { user: UserListEntry }) {
  const profilePhoto = imageSrc(user.profilePhoto);
  const name = userDisplayName(user);

  if (profilePhoto) {
    return (
      <SafeImage
        src={profilePhoto}
        alt={name}
        width={48}
        height={48}
        className="h-12 w-12 rounded-full object-cover"
      />
    );
  }

  const initials = `${user.firstName?.charAt(0) ?? ""}${user.lastName?.charAt(0) ?? ""}`.toUpperCase() || "U";

  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-sm font-bold text-accent-foreground">
      {initials}
    </div>
  );
}

function UserRow({ user }: { user: UserListEntry }) {
  const name = userDisplayName(user);

  return (
    <Link
      to={`/users/${user.id}`}
      className="hover-raise flex items-center gap-4 rounded-2xl bg-canvas p-4 hover:bg-surface"
    >
      <UserAvatar user={user} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-bold">{name}</p>
          {user.is_admin ? (
            <span className="rounded-full bg-accent-subtle px-2 py-0.5 text-[10px] font-bold text-accent-foreground">
              Admin
            </span>
          ) : null}
        </div>
        <p className="mt-1 truncate text-sm text-muted">{user.email ?? user.country ?? "No email"}</p>
        <p className="mt-1 text-xs text-muted">Joined {formatDate(user.created_at)}</p>
      </div>
      <div className="hidden shrink-0 gap-4 text-xs font-semibold text-muted sm:flex">
        <span className="inline-flex items-center gap-1">
          <Car className="h-3.5 w-3.5" />
          {formatNumber(user.carCount)}
        </span>
        <span className="inline-flex items-center gap-1">
          <Wrench className="h-3.5 w-3.5" />
          {formatNumber(user.serviceCount)}
        </span>
      </div>
    </Link>
  );
}

export function UsersExplorer({ users }: { users: UserListEntry[] }) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => {
      const haystack = [
        user.firstName,
        user.lastName,
        user.email,
        user.country,
        user.userID,
        user.phoneNumber,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [users, search]);

  return (
    <Workspace>
      <WorkspaceSection className="border-b border-border/30 p-4 sm:p-5">
        <FieldLabel icon={<Search className="h-3 w-3" />}>Search</FieldLabel>
        <Input
          placeholder="Name, email, country…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </WorkspaceSection>

      <WorkspaceSection border className="p-4 sm:p-5">
        <p className="mb-5 text-sm text-muted">
          <span className="font-bold text-foreground">{filtered.length}</span> users
        </p>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="font-bold">No matches</p>
            <p className="mt-1 text-sm text-muted">Adjust your search to see results.</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {filtered.map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
          </div>
        )}
      </WorkspaceSection>
    </Workspace>
  );
}
